import { Button, Card, ListItem } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Activity, BellDot, ChevronRight, Lightbulb, MessageCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { chatListSessionsOptions } from "@/api";
import { QuerySection, SectionEmpty } from "@/components/section";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { useInbox } from "@/features/knowledge/inbox/useInbox";
import { ActivityList } from "@/features/spaces/ActivityList";
import { useRecentActivity } from "@/features/spaces/useRecentActivity";
import { formatRelativeTime } from "@/i18n/format";

function HomeSection({
  title,
  className = "",
  action,
  children,
}: Readonly<{ title: string; className?: string; action?: ReactNode; children: ReactNode }>) {
  return (
    <section aria-label={title} className={`flex flex-col gap-2 ${className}`}>
      <div className="flex min-h-8 items-center justify-between">
        <Text as="h2" variant="eyebrow" tone="tertiary">
          {title}
        </Text>
        {action && <div className="-mr-3">{action}</div>}
      </div>
      {children}
    </section>
  );
}

export function RecentChats() {
  const { t } = useTranslation("home", { keyPrefix: "chats" });
  const navigate = useNavigate();
  const chats = useQuery({ ...chatListSessionsOptions({ query: { page_size: 5 } }), select: (r) => r.data });
  return (
    <HomeSection
      title={t("title")}
      action={
        <Button variant="text" size="sm" trailingIcon={<ChevronRight size={12} />} onClick={() => navigate("/chats")}>
          {t("all")}
        </Button>
      }
    >
      <QuerySection
        query={chats}
        isEmpty={(list) => list.length === 0}
        empty={{ icon: <MessageCircle size={18} />, hint: t("empty") }}
      >
        {(list) => (
          <Card className="overflow-hidden !p-2">
            <ul>
              {list.map((chat, index) => (
                <li key={chat.uuid}>
                  <ListItem
                    divider={index > 0}
                    onClick={() => navigate(`/chat/${chat.uuid}`)}
                    leading={<MessageCircle size={15} className="text-content-tertiary" />}
                    description={chat.spaceIsPersonal ? t("privateSpace") : (chat.spaceName ?? undefined)}
                    trailing={
                      <Text as="span" variant="caption" tone="tertiary" className="shrink-0">
                        {formatRelativeTime(chat.updatedAt)}
                      </Text>
                    }
                  >
                    {chat.title || t("untitled")}
                  </ListItem>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </QuerySection>
    </HomeSection>
  );
}

/**
 * What's waiting on me. So far: facts suggested from my chats, to review before they reach a
 * wiki (knowledge graph). Other kinds (e.g. feedback requests) come later (docs/design/web/WEB_SPACES_PLAN.md).
 */
export function NeedsAttention() {
  const { t } = useTranslation("home", { keyPrefix: "attention" });
  const navigate = useNavigate();
  const { knowledgeExtractionEnabled } = useCapabilities();
  const inbox = useInbox({ enabled: knowledgeExtractionEnabled });
  const suggestions = knowledgeExtractionEnabled ? (inbox.data?.pendingCount ?? 0) : 0;
  return (
    <HomeSection title={t("title")}>
      {suggestions > 0 ? (
        <Card className="overflow-hidden !p-2">
          <ListItem
            leading={<Lightbulb size={16} aria-hidden className="text-content-tertiary" />}
            description={t("suggestionsHint")}
            onClick={() => navigate("/knowledge/inbox")}
          >
            {t("suggestions", { count: suggestions })}
          </ListItem>
        </Card>
      ) : (
        <SectionEmpty icon={<BellDot size={18} />} hint={t("empty")} />
      )}
    </HomeSection>
  );
}

/** Newest activity across my spaces. */
export function SpacesActivityFeed() {
  const { t } = useTranslation("home", { keyPrefix: "activity" });
  const activity = useRecentActivity(8);
  return (
    <HomeSection title={t("title")} className="lg:col-span-2">
      <QuerySection
        query={activity}
        isEmpty={(list) => list.length === 0}
        empty={{ icon: <Activity size={18} />, hint: t("empty") }}
      >
        {(items) => <ActivityList items={items} showSpace />}
      </QuerySection>
    </HomeSection>
  );
}
